import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  FileText, FileSpreadsheet, Download, LogIn, LogOut, Building2, Loader2, CalendarDays,
} from "lucide-react";
import { formatCurrency } from "@/lib/status";

const todayStr = () => new Date().toISOString().slice(0, 10);

const Section = ({ title, icon: Icon, accent, children, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={testid}>
    <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100">
      <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${accent}`}><Icon className="h-4 w-4" /></div>
      <h3 className="font-display font-semibold text-slate-900">{title}</h3>
    </div>
    {children}
  </div>
);

const flatLabel = (r) => `${r.tower}-${r.flat_number}`;

export default function Reports() {
  const [date, setDate] = useState(todayStr());
  const [activity, setActivity] = useState(null);
  const [regs, setRegs] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const [a, r] = await Promise.all([
        api.get(`/reports/daily-activity`, { params: { date: d } }),
        api.get(`/reports/daily-registrations`, { params: { date: d } }),
      ]);
      setActivity(a.data);
      setRegs(r.data);
    } catch {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(date); }, [date, load]);

  const downloadReport = async (kind) => {
    const t = toast.loading(`Preparing ${kind.toUpperCase()} report…`);
    try {
      const { data } = await api.get(`/reports/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = kind === "excel" ? "society_flat_report.xlsx" : "society_flat_report.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${kind.toUpperCase()} downloaded`, { id: t });
    } catch {
      toast.error("Report failed", { id: t });
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase font-mono tracking-[0.18em] text-slate-400 mb-1">Society Reports</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Reports</h1>
        </div>
      </div>

      {/* Full inventory export */}
      <Section title="Full Inventory & Tenancy Report" icon={Download} accent="bg-slate-100 text-slate-700" testid="full-report-section">
        <div className="p-5 flex flex-wrap gap-3">
          <Button onClick={() => downloadReport("pdf")} className="bg-[#0F2942] gap-2" data-testid="report-pdf-button"><FileText className="h-4 w-4" /> Download PDF</Button>
          <Button onClick={() => downloadReport("excel")} variant="outline" className="gap-2" data-testid="report-excel-button"><FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Download Excel (.xlsx)</Button>
          <p className="text-sm text-slate-400 self-center">Includes every flat, owner dossier, and all move-in / move-out records with totals.</p>
        </div>
      </Section>

      {/* Date picker */}
      <div className="flex items-center gap-3">
        <div className="relative">
          <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="pl-9 w-48 bg-white" data-testid="report-date-input" />
        </div>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily move-in */}
        <Section title="Daily Move-Ins" icon={LogIn} accent="bg-emerald-50 text-emerald-600" testid="daily-movein-section">
          <div className="p-5 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">{activity?.move_ins?.length || 0} move-in(s) on {date}</span>
              <span className="font-mono font-semibold text-emerald-700">Collected: {formatCurrency(activity?.move_in_total || 0)}</span>
            </div>
            {(activity?.move_ins || []).length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">No move-ins on this date.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {activity.move_ins.map((r, i) => (
                  <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="py-2.5 flex items-center justify-between gap-3" data-testid={`movein-row-${i}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{r.tenant_name} <span className="text-slate-400 font-mono text-xs">· {r.tenant_phone || "—"}</span></p>
                      <p className="text-xs text-slate-400 font-mono">{flatLabel(r)} · Floor {r.floor}{r.gate_pass_code ? ` · ${r.gate_pass_code}` : ""}</p>
                    </div>
                    <span className="text-sm font-mono text-emerald-700 whitespace-nowrap">{formatCurrency(r.amount)}<span className="text-[10px] text-slate-400 ml-1">{r.mode || ""}</span></span>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </Section>

        {/* Daily move-out */}
        <Section title="Daily Move-Outs" icon={LogOut} accent="bg-rose-50 text-rose-600" testid="daily-moveout-section">
          <div className="p-5 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">{activity?.move_outs?.length || 0} move-out(s) on {date}</span>
              <span className="font-mono font-semibold text-rose-700">Collected: {formatCurrency(activity?.move_out_total || 0)}</span>
            </div>
            {(activity?.move_outs || []).length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">No move-outs on this date.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {activity.move_outs.map((r, i) => (
                  <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="py-2.5 flex items-center justify-between gap-3" data-testid={`moveout-row-${i}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{r.tenant_name} <span className="text-slate-400 font-mono text-xs">· {r.tenant_phone || "—"}</span></p>
                      <p className="text-xs text-slate-400 font-mono">{flatLabel(r)} · Floor {r.floor}</p>
                    </div>
                    <span className="text-sm font-mono text-rose-700 whitespace-nowrap">{formatCurrency(r.amount)}<span className="text-[10px] text-slate-400 ml-1">{r.mode || ""}</span></span>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </Section>
      </div>

      {/* Daily new flat registrations */}
      <Section title="New Flat Registrations" icon={Building2} accent="bg-blue-50 text-blue-600" testid="daily-registration-section">
        <div className="p-5">
          <p className="text-sm text-slate-500 mb-3">{regs?.count || 0} flat(s) registered on {date}</p>
          {(regs?.flats || []).length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">No flats registered on this date.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase font-mono tracking-wide text-slate-400 border-b border-slate-100">
                    <th className="py-2 pr-4">Tower-Flat</th>
                    <th className="py-2 pr-4">Floor</th>
                    <th className="py-2 pr-4">BHK</th>
                    <th className="py-2 pr-4">Owner</th>
                    <th className="py-2 pr-4">Phone</th>
                    <th className="py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {regs.flats.map((f, i) => (
                    <tr key={i} className="border-b border-slate-50" data-testid={`registration-row-${i}`}>
                      <td className="py-2 pr-4 font-mono font-medium text-slate-800">{f.tower}-{f.flat_number}</td>
                      <td className="py-2 pr-4 text-slate-600">{f.floor}</td>
                      <td className="py-2 pr-4 text-slate-600">{f.bhk_type}</td>
                      <td className="py-2 pr-4 text-slate-800">{f.owner_name}</td>
                      <td className="py-2 pr-4 text-slate-500 font-mono text-xs">{f.owner_phone || "—"}</td>
                      <td className="py-2 text-slate-600 capitalize">{(f.status || "").replace("_", " ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Section>
    </div>
  );
}
